import { Alert, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Calendar } from '@ophelos/ui/components/calendar';
import { BadgePercent, ChevronRight, CircleAlert, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { AffordabilityCommitment } from '@/components/payments/AffordabilityCommitment';
import { OfferOutcome } from '@/components/payments/OfferOutcome';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { PaymentCountStepper } from '@/components/payments/PaymentCountStepper';
import { PlanScheduleList } from '@/components/payments/PlanScheduleList';
import { PlanSummaryHero } from '@/components/payments/PlanSummaryHero';
import { SegmentedControl } from '@/components/payments/SegmentedControl';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useOfferGate } from '@/hooks/useOfferGate';
import { usePayerEmail } from '@/hooks/usePayerEmail';
import { useTranslation } from '@/hooks/useTranslation';
import { formatMoneyInLanguage, fromSubunits } from '@/lib/currency';
import { planPrice } from '@/lib/debtHeadline';
import { baseInstalmentSubunits, buildPlanSchedule, oneMonthAhead, toScheduleDate } from '@/lib/payment-schedule';
import { parsePlanCount, parsePlanStartDate } from '@/lib/redirect';
import { selectPaymentTier } from '@/lib/paymentPlans';
import type { PaymentPlanConfiguration } from '@/types';

export interface PaymentPlanData {
  startDate: Date;
  frequency: 'weekly' | 'biweekly' | 'monthly';
  paymentCount: number;
  paymentAmount: number;
  /** Chosen on the method step that follows configuration; absent until then. */
  paymentMethod?: PaymentMethod;
  /** Plan total the schedule is built from — discounted when an offer applies. */
  totalAmount: number;
  /** Credit attributed to this debt, taken off after any discount. */
  creditApplied?: number;
  /** Balance before the offer discount; set only when one applies. */
  originalTotalAmount?: number;
  /**
   * Offers `totalAmount` was priced against, so the authorising screen can refuse to create a
   * plan on pricing that lapsed while it was open. Absent when no discount applied.
   */
  pricedOfferIds?: string[];
}

interface PaymentPlanTabProps {
  caseId: string;
  totalAmount: number;
  /** Credit attributed to this debt, taken off after any discount. */
  creditApplied?: number;
  currency?: string;
  paymentPlanConfig?: PaymentPlanConfiguration;
  onContinue?: () => void;
  /** Called once the configured plan has passed the offer gate; the parent picks the method next. */
  onConfigured?: (planData: PaymentPlanData) => void;
  /**
   * Message from the parent explaining why the customer was returned here — set when a
   * reviewed plan was refused because its pricing lapsed. Shown in the tab's own alert.
   */
  returnNotice?: string | null;
  /** Deep-link prefill from an operator-sent payment link (advisory; clamped to config). */
  initialFrequency?: string | null;
  initialStartDate?: string | null;
  initialPaymentCount?: string | null;
  /**
   * The plan a verdict has already been taken on, held by the parent across "Edit plan". Terms
   * matching it are not resubmitted — see `samePlanTerms`.
   */
  decidedPlan?: PaymentPlanData | null;
  /**
   * Called immediately before an offer is submitted, so the parent can drop any verdict it was
   * holding: this submission may supersede it, and a superseded verdict must not be reused.
   */
  onOfferSubmitted?: () => void;
  /** True when the customer has just come back from a budget assessment. */
  assessmentReturned?: boolean;
}

/**
 * Whether a plan is the one a verdict was already taken on.
 *
 * The start date is compared by calendar day, not by instant: the first submission sends a
 * mount-time `new Date()` carrying a time of day, while "Edit plan" re-hydrates the tab through
 * a `yyyy-MM-dd` deep-link string that parses to local midnight. An instant comparison would
 * never match and every edit would resubmit.
 */
export function samePlanTerms(a: PaymentPlanData, b: Omit<PaymentPlanData, 'paymentAmount'>): boolean {
  return (
    a.frequency === b.frequency &&
    a.paymentCount === b.paymentCount &&
    a.totalAmount === b.totalAmount &&
    toScheduleDate(a.startDate) === toScheduleDate(b.startDate)
  );
}

export function PaymentPlanTab({
  caseId,
  totalAmount: propTotalAmount,
  creditApplied = 0,
  currency = 'EUR',
  paymentPlanConfig,
  onContinue: _onContinue,
  onConfigured,
  initialFrequency,
  initialStartDate,
  initialPaymentCount,
  returnNotice = null,
  decidedPlan = null,
  onOfferSubmitted,
  assessmentReturned = false,
}: PaymentPlanTabProps) {
  const { t, currentLanguage } = useTranslation();
  const { isPreviewMode, config } = useConfiguration();
  const { activeOffers } = useDebtData();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { needsEmail, payerEmail, setPayerEmail, emailValid, persistPayerEmail } = usePayerEmail();
  // Above the early returns below, or it becomes a conditional hook.
  const offerGate = useOfferGate({ caseId, basePath: `/payment/${caseId}` });

  // Active offer applies a discount to the plan total when the payment_plan option is enabled.
  // Discount never shows on Partial Payment (different tab — locked decision).
  const offer = activeOffers[caseId];
  // The credit attributed to this debt comes off after the discount, matching the account
  // surfaces and the figure the dashboard card showed before the customer got here.
  const { discounted, amount: totalAmount } = planPrice(propTotalAmount, creditApplied, offer);
  const showDiscount = discounted != null;
  const discountedTotal = discounted ?? propTotalAmount;
  const planPct = offer?.configSnapshot.options?.payment_plan?.percentage ?? 0;
  const planSavingsAmount = showDiscount ? propTotalAmount - discountedTotal : 0;

  const configuredPlanMethods = config.payment?.planPaymentMethods as PaymentMethod[] | undefined;
  const availablePlanMethods = useMemo<PaymentMethod[]>(() => configuredPlanMethods ?? [], [configuredPlanMethods]);

  // Get available frequencies from config, defaulting to monthly only
  const configuredFrequencies = paymentPlanConfig?.paymentFrequencies ?? ['monthly'];

  // Map config frequencies to UI-friendly format (bi-weekly in config -> biweekly in UI)
  const frequencies = useMemo(() => {
    const allFrequencies = [
      { value: 'weekly' as const, label: t('paymentPlan.weekly'), configKey: 'weekly' },
      { value: 'biweekly' as const, label: t('paymentPlan.biweekly'), configKey: 'bi-weekly' },
      { value: 'monthly' as const, label: t('paymentPlan.monthly'), configKey: 'monthly' },
    ];
    return allFrequencies.filter((f) => configuredFrequencies.includes(f.configKey as 'monthly' | 'bi-weekly' | 'weekly'));
  }, [t, configuredFrequencies]);

  // Select tier based on debt amount
  const tier = selectPaymentTier(totalAmount, paymentPlanConfig);

  // Get default frequency - prefer longest duration (monthly > biweekly > weekly)
  const defaultFrequency = frequencies.length > 0 ? frequencies[frequencies.length - 1].value : 'monthly';

  const [startDate, setStartDate] = useState<Date | undefined>(() => parsePlanStartDate(initialStartDate) ?? new Date());
  // The segment is its own state: "Later" keeps today selected in the calendar until the
  // customer picks another day, so it cannot be derived from whether the date is today.
  const [firstPayment, setFirstPayment] = useState<'today' | 'later'>(() => {
    const restored = parsePlanStartDate(initialStartDate);
    return restored && toScheduleDate(restored) !== toScheduleDate(new Date()) ? 'later' : 'today';
  });
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>(() =>
    (initialFrequency === 'weekly' || initialFrequency === 'biweekly' || initialFrequency === 'monthly') &&
    frequencies.some((f) => f.value === initialFrequency)
      ? initialFrequency
      : defaultFrequency,
  );
  const [paymentCount, setPaymentCount] = useState(() => parsePlanCount(initialPaymentCount) ?? 6);

  // Memoize date boundaries to avoid recreating on every render
  const { today, maxDate, todayOnly, maxDateOnly } = useMemo(() => {
    const today = new Date();
    const maxDate = oneMonthAhead(today);
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    return { today, maxDate, todayOnly, maxDateOnly };
  }, []);

  // If no tier matches, payment plans are not available
  if (!tier) {
    const minEligible = paymentPlanConfig?.minEligibleDebt ?? 0;
    return (
      <div className="w-full h-full flex items-center justify-center px-4">
        <Alert variant="subtle" className="max-w-md">
          <CircleAlert className="size-4" />
          <AlertTitle>{t('paymentPlan.notAvailable')}</AlertTitle>
          <AlertDescription>
            {t('paymentPlan.availableForDebts', { amount: formatMoneyInLanguage(minEligible, currentLanguage, currency) })}
            {totalAmount < minEligible &&
              ` ${t('paymentPlan.belowThreshold', { amount: formatMoneyInLanguage(totalAmount, currentLanguage, currency) })}`}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Operator has no payment methods configured for plans
  if (availablePlanMethods.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center px-4">
        <Alert variant="subtle" className="max-w-md">
          <CircleAlert className="size-4" />
          <AlertTitle>{t('paymentPlan.notAvailable')}</AlertTitle>
          <AlertDescription>{t('paymentPlan.noMethodsConfigured')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const maxDurationMonths = tier.maxDurationMonths;

  // Calculate max payments based on frequency (maxDurationMonths is always in months)
  const maxPayments = useMemo(() => {
    if (frequency === 'weekly') {
      return maxDurationMonths * 4; // ~4 weeks per month
    }
    if (frequency === 'biweekly') {
      return maxDurationMonths * 2; // ~2 bi-weekly periods per month
    }
    return maxDurationMonths; // monthly
  }, [frequency, maxDurationMonths]);

  // Clamp paymentCount to valid range when maxPayments changes
  useEffect(() => {
    setPaymentCount((prev) => Math.min(Math.max(prev, 2), maxPayments));
  }, [maxPayments]);

  // Quote the instalment the plan charges, not the average — see baseInstalmentSubunits.
  // `onReview`/`executePaymentPlan` still take the unrounded total, so the plan's own
  // split stays exact; flooring here would drop the remainder off the total.
  const baseInstalment = fromSubunits(baseInstalmentSubunits(totalAmount, paymentCount));

  const isToday =
    startDate &&
    startDate.getDate() === today.getDate() &&
    startDate.getMonth() === today.getMonth() &&
    startDate.getFullYear() === today.getFullYear();

  // Memoize calendar disabled check — the calendar's own range never depends
  // on the selected method; only the method list reacts to the date.
  const isDateDisabled = useCallback(
    (date: Date) => {
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return dateOnly < todayOnly || dateOnly > maxDateOnly;
    },
    [todayOnly, maxDateOnly],
  );

  const handleContinue = async () => {
    if (!startDate) {
      setError(t('paymentPlan.selectStartDate'));
      return;
    }

    if (!emailValid) {
      setError(t('paymentPlan.emailRequired'));
      return;
    }

    // Persist a customer-supplied email (when none usable on file) up front — before any
    // verification round-trip that could remount this tab and lose the entered value. The
    // server then resolves it from file at checkout.
    try {
      await persistPayerEmail();
    } catch {
      setError(t('paymentPlan.emailRequired'));
      return;
    }

    const configured = {
      startDate,
      frequency,
      paymentCount,
      paymentAmount: totalAmount / paymentCount,
      totalAmount,
      originalTotalAmount: showDiscount ? propTotalAmount : undefined,
      pricedOfferIds: showDiscount && offer ? [offer.offerId] : undefined,
    };

    // Every method is gated here, ahead of any review screen: nobody should read an
    // "Authorise card & pay" screen and only then be told they need to answer budget questions.
    //
    // `isLoading` is held across both calls so the CTA cannot be pressed twice — a second
    // submission would land on `uq_plan_requests_pending_*` as a conflict.
    setIsLoading(true);
    // The alert renders `error ?? offerGate.error`, so a stale checkout failure would otherwise
    // outrank — and hide — the refusal this submission is about to produce.
    setError(null);

    // A verdict already taken on these exact numbers stands, so "Edit plan" and straight back
    // out again records one accepted offer rather than one per press. Any real change moves a
    // compared field — including a discount lapsing, which moves `totalAmount`.
    if (!decidedPlan || !samePlanTerms(decidedPlan, configured)) {
      onOfferSubmitted?.();
      const decided = await offerGate.submit({ frequency, count: paymentCount, startDate, totalAmount });
      if (decided.go === 'assessment') {
        navigate(decided.url);
        return;
      }
      if (decided.go === 'stop') {
        setIsLoading(false);
        return;
      }
    }

    // The method is chosen on the next screen; card plans go on to review from there.
    setIsLoading(false);
    onConfigured?.(configured);
  };

  // After every hook, so a verdict arriving mid-mount cannot change the hook count. It replaces
  // the tab body rather than sitting beside it, so its fixed CTA bar is the only one on screen.
  //
  // Referred verdicts only: an accepted offer goes straight on to review or setup, so it never
  // holds an outcome here.
  if (offerGate.outcome) {
    const held = offerGate.outcome;
    return (
      <OfferOutcome
        outcome={held.outcome}
        terms={held.terms}
        currency={currency}
        frequencyLabel={frequencies.find((f) => f.value === held.terms.frequency)?.label ?? held.terms.frequency}
        onChoosePlan={offerGate.reset}
        onDone={() => navigate(isPreviewMode ? '/dashboard?preview=true' : '/dashboard')}
        isBusy={isLoading}
        error={error}
      />
    );
  }

  return (
    <div className="w-full flex-1 min-h-0 stack items-center px-2 xs:px-4">
      <div className="flex-1 w-full stack space-y-4">
        <div className="max-w-md mx-auto w-full">
          {showDiscount && (
            <Alert variant="warning" className="mb-6">
              <BadgePercent />
              <AlertTitle>{t('discount.percentApplied', { pct: Math.round(planPct * 100) })}</AlertTitle>
              <AlertDescription className="flex">
                <Trans
                  i18nKey="discount.youSaveAcrossPlan"
                  components={{
                    amount: <CurrencyDisplay amount={planSavingsAmount} currency={currency} className="font-semibold inline-flex gap-0" />,
                  }}
                />
              </AlertDescription>
            </Alert>
          )}

          <section className="stack gap-6 w-full mb-8">
            <div className="stack gap-1.5">
              <h3 className="text-sm font-semibold">{t('paymentPlan.numberOfPayments')}</h3>
              <PaymentCountStepper count={paymentCount} onChange={setPaymentCount} min={2} max={maxPayments} disabled={isLoading} />
            </div>

            <div className="stack gap-1.5">
              <h3 className="text-sm font-semibold">{t('paymentPlan.frequency')}</h3>
              <SegmentedControl
                options={frequencies}
                value={frequency}
                onChange={setFrequency}
                disabled={isLoading}
                aria-label={t('paymentPlan.frequency')}
              />
            </div>

            <PlanSummaryHero
              instalment={baseInstalment}
              currency={currency}
              paymentCount={paymentCount}
              frequencyLabel={frequencies.find((f) => f.value === frequency)?.label ?? ''}
              total={totalAmount}
              originalInstalment={showDiscount ? propTotalAmount / paymentCount : undefined}
              originalTotal={showDiscount ? propTotalAmount : undefined}
              expiresAt={offer?.expiresAt}
            />
          </section>

          <section className="stack gap-1.5 w-full">
            <h3 className="text-sm font-semibold">{t('paymentPlan.firstPayment')}</h3>
            <SegmentedControl
              options={[
                { value: 'today', label: t('paymentPlan.today') },
                { value: 'later', label: t('paymentPlan.later') },
              ]}
              value={firstPayment}
              onChange={(choice) => {
                setFirstPayment(choice);
                if (choice === 'today' || !startDate) setStartDate(new Date());
              }}
              disabled={isLoading}
              aria-label={t('paymentPlan.firstPayment')}
            />
            {firstPayment === 'later' && (
              <div className="rounded-2xl border bg-card p-2 mt-2 w-full max-w-xs mx-auto">
                <Calendar
                  mode="single"
                  selected={startDate}
                  captionLayout="dropdown-months"
                  startMonth={today}
                  endMonth={maxDate}
                  disabled={isDateDisabled}
                  onSelect={(date) => date && setStartDate(date)}
                  className="w-full bg-transparent "
                  classNames={{ root: 'w-full' }}
                />
              </div>
            )}
          </section>

          {startDate && (
            <PlanScheduleList
              instalments={buildPlanSchedule(totalAmount, paymentCount, startDate, frequency)}
              currency={currency}
              totalAmount={totalAmount}
              startsToday={!!isToday}
            />
          )}

          {needsEmail && <PayerEmailField id="plan-payer-email" value={payerEmail} onChange={setPayerEmail} />}
        </div>

        <div className="max-w-md w-full mx-auto stack gap-4">
          {/* Back from the assessment. Deliberately says nothing about the outcome: a situation
              disclosed on the assessment refers the offer, and that check runs first. */}
          {assessmentReturned && (
            <Alert variant="subtle">
              <CircleAlert />
              <AlertDescription>{t('paymentOffer.assessmentSaved')}</AlertDescription>
            </Alert>
          )}

          {/* Error Alert */}
          {(error || offerGate.error || returnNotice) && (
            <Alert variant="destructive">
              <CircleAlert />
              <AlertDescription>{error ?? offerGate.error ?? returnNotice}</AlertDescription>
            </Alert>
          )}
        </div>

        <AffordabilityCommitment />
      </div>

      <BottomActionBar>
        <Button onClick={handleContinue} size="xl" className="group w-full max-w-sm" disabled={isLoading || !emailValid}>
          {isLoading ? (
            <>
              <Loader2 className="size-5 mr-2 animate-spin" />
              {t('verify.processing')}
            </>
          ) : (
            <>
              {t('paymentPlan.setupPayment')}
              <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </BottomActionBar>
    </div>
  );
}
